// ── Battle SFX manager ────────────────────────────────────────────────────────
// Web Audio playback for the inter-round battle. Each trigger spawns its own
// independent voice from a preloaded buffer, so rapid/overlapping shots layer
// (no cutting-off) — they all play. A master compressor on the bus keeps stacked
// peaks from clipping, and per-voice gain + pitch jitter keeps repeats from
// sounding like one machine-gun loop. No hard voice cap.
//
// Files live in /public and are referenced by name below. initSound() is safe to
// call on mount (creates a suspended context + preloads); call it again from a
// user gesture (the REVEAL click) to resume — browsers require a gesture to start
// audio.

const FILES = {
    launch:     'drone_sound_effect.mp3',  // combat drones lifting off — the quadcopter buzz per shot
    collision:  'explosion_missile_or_rocket_collision_audio_01.mp3',
    strike:     'explosion_plot_or_structure_audio_01.mp3',
    scoreboard: 'scoreboard_sound.mp3',   // a kill-bar structure icon dies (micro explosion)
    resupply:   'resupply_land_soundeffect_02.mp3',  // parachute crate touches down on its slot
    parachute:  'parachute open sound effect.mp3',  // chute snaps open as a crate starts falling
    drone:      'Spy Airplane Sound Effect.mp3',  // recon plane — flight + hover during the scan phase
}

// Base level per sound — the frequent ones sit lower so the barrage doesn't bury
// the punchier strike.
// (parachute/resupply sit above 1 — those source clips are recorded quiet, ~-12dBFS peak)
const GAIN = { launch: 0.6, collision: 0.72, strike: 0.68, scoreboard: 0.8, resupply: 1.4, parachute: 1.4, drone: 0.9 }

let ctx = null
let bus = null            // compressor → output gain → destination
const buffers = {}        // name → AudioBuffer
const voices = new Set()  // live { src, g } voices — for graceful fade on interrupt
let loadStarted = false
let muted = false

function fileUrl(file) {
    const base = process.env.PUBLIC_URL || ''
    return `${base}/${file}`
}

async function loadBuffers() {
    if (loadStarted || !ctx) return
    loadStarted = true
    await Promise.all(Object.entries(FILES).map(async ([name, file]) => {
        try {
            const res = await fetch(fileUrl(file))
            const arr = await res.arrayBuffer()
            buffers[name] = await ctx.decodeAudioData(arr)
        } catch {
            /* missing/undecodable clip — that trigger just stays silent */
        }
    }))
}

// Create the context (suspended) + start preloading, or resume if already created.
// Idempotent: first call boots, later calls just resume on a gesture.
export function initSound() {
    if (ctx) {
        if (ctx.state === 'suspended') ctx.resume()
        return
    }
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return
    try { ctx = new AC() } catch { ctx = null; return }

    const comp = ctx.createDynamicsCompressor()
    comp.threshold.value = -12
    comp.knee.value      = 24
    comp.ratio.value     = 6
    comp.attack.value    = 0.003
    comp.release.value   = 0.18
    const out = ctx.createGain()
    out.gain.value = 0.9
    comp.connect(out)
    out.connect(ctx.destination)
    bus = comp

    loadBuffers()
    if (ctx.state === 'suspended') ctx.resume()
}

export function setSoundMuted(m) { muted = !!m }
export function isSoundMuted()   { return muted }

// Play one voice. Options:
//   gainScale — duck/boost this specific hit.
//   duration  — seconds; if set, the voice is cut at that point (with a tiny fade
//               to avoid a click). Used so a launch whoosh ends the instant its
//               missile collides or strikes.
//   offset    — seconds into the buffer to start from. Used to skip a clip's
//               leading silence so the trigger lines up with its audible attack.
export function playSound(name, { gainScale = 1, duration, offset = 0 } = {}) {
    if (muted || !ctx || !bus) return
    const buffer = buffers[name]
    if (!buffer) return
    if (ctx.state === 'suspended') ctx.resume()

    const src = ctx.createBufferSource()
    src.buffer = buffer
    src.playbackRate.value = 1 + (Math.random() * 0.12 - 0.06)   // ±6% pitch jitter

    const g = ctx.createGain()
    g.gain.value = (GAIN[name] ?? 1) * gainScale * (0.85 + Math.random() * 0.15)

    src.connect(g)
    g.connect(bus)

    // Track the live voice so fadeOutAll() can taper it; drop it when it ends.
    const voice = { src, g }
    voices.add(voice)
    src.onended = () => voices.delete(voice)

    src.start(0, offset)

    if (duration && duration > 0) {
        const stopAt    = ctx.currentTime + duration
        const fadeStart = Math.max(ctx.currentTime, stopAt - 0.04)
        g.gain.setValueAtTime(g.gain.value, fadeStart)
        g.gain.linearRampToValueAtTime(0.0001, stopAt)
        try { src.stop(stopAt + 0.02) } catch { /* already ended */ }
    }
}

// Gently taper every live voice to silence over `seconds`, then stop it. Used when a
// battle is interrupted (a new clash begins, or the board unmounts) so still-playing
// shots fade out instead of being chopped. Voices left alone simply finish on their own.
export function fadeOutAll(seconds = 0.6) {
    if (!ctx) return
    const now = ctx.currentTime
    const end = now + Math.max(0.05, seconds)
    voices.forEach(({ src, g }) => {
        try {
            if (g.gain.cancelAndHoldAtTime) g.gain.cancelAndHoldAtTime(now)
            else { g.gain.cancelScheduledValues(now); g.gain.setValueAtTime(g.gain.value, now) }
            g.gain.linearRampToValueAtTime(0.0001, end)
            src.stop(end + 0.03)
        } catch { /* voice already ended */ }
    })
}
